import { IDataSource } from "client/Data/common";
import { IPageable, PaginationInfo } from "client/Data/pagination";
import { normalizeForSearch } from "client/Data/searching";
import {
	DataSourceSortableState,
	DataSourceSortDirection,
	nativeSortFunction
} from "client/Data/sorting";
import { useMemo } from "react";
import { useValueEqualMemo } from "./useValueEqualMemo";
import { getPropertyPathResolver } from "client/Misc/utils";

export function createLocalDataSource<
	TDataItem,
	TFilterState = undefined,
	TGlobalContext = void
>(): LocalDataSourceConfigurator<
	TDataItem,
	TFilterState,
	TGlobalContext
> {
	const result = new LocalDataSourceConfiguratorImplementation<
		TDataItem,
		TFilterState,
		TGlobalContext
	>();

	return result;
}

type LocalDataSourceConfigurator<TDataItem, TFilterState, TGlobalContext> = {
	setConfiguration<TConfiguration extends LocalDataSourceConfiguration<TDataItem, TFilterState, TGlobalContext>>(
		configuration: TConfiguration
	): ResultPartLocalDataSourceBuilder<TDataItem, TConfiguration, TGlobalContext>;
};

type LocalDataSourceConfiguration<TDataItem, TFilterState, TGlobalContext> =
	& FilteringPartLocalDataSourceConfiguration<TDataItem, TFilterState, TGlobalContext>
	& SearchPartLocalDataSourceConfiguration<TDataItem, TGlobalContext>
	& PaginationPartLocalDataSourceConfiguration;

type HasFilteringPartLocalDataSourceConfigurationOptions<TDataItem, TFilterState, TGlobalContext> = {
	readonly applyFilter: (items: ReadonlyArray<TDataItem>, filterState: TFilterState, globalContext: TGlobalContext) => ReadonlyArray<TDataItem>;
};

type HasFilteringPartLocalDataSourceConfiguration<TDataItem, TFilterState, TGlobalContext> = {
	filtering: HasFilteringPartLocalDataSourceConfigurationOptions<TDataItem, TFilterState, TGlobalContext>;
};
type FilteringPartLocalDataSourceConfiguration<TDataItem, TFilterState, TGlobalContext> =
	| HasFilteringPartLocalDataSourceConfiguration<TDataItem, TFilterState, TGlobalContext>
	| { filtering?: false; };

type HasSearchPartLocalDataSourceConfigurationOptions<TDataItem, TGlobalContext> = {
	readonly doesMatchItem: (item: TDataItem, searchTerm: string, globalContext: TGlobalContext) => boolean;
};

type HasSearchPartLocalDataSourceConfiguration<TDataItem, TGlobalContext> = {
	search: HasSearchPartLocalDataSourceConfigurationOptions<TDataItem, TGlobalContext>;
};
type SearchPartLocalDataSourceConfiguration<TDataItem, TGlobalContext> =
	| HasSearchPartLocalDataSourceConfiguration<TDataItem, TGlobalContext>
	| { search?: false; };

type HasPaginationPartLocalDataSourceConfiguration = {
	readonly hasPagination: true;
};
type PaginationPartLocalDataSourceConfiguration =
	| HasPaginationPartLocalDataSourceConfiguration
	| { hasPagination?: false; };

type ResultPartLocalDataSourceBuilder<TDataItem, TConfiguration, TGlobalContext> = {
	getHook(): UseLocalDataSource<TDataItem, TConfiguration, TGlobalContext>;
};

type UseLocalDataSource<TDataItem, TConfiguration, TGlobalContext> = (
	items: ReadonlyArray<TDataItem>,
	options: UseLocalDataSourceOptions<TDataItem, TConfiguration, TGlobalContext>
) => LocalDataSource<TDataItem, TConfiguration>;

type UseLocalDataSourceOptions<TDataItem, TConfiguration, TGlobalContext> =
	& SortingPartUseLocalDataSourceOptions<TDataItem>
	& FilteringPartUseLocalDataSourceOptions<TDataItem, TConfiguration, TGlobalContext>
	& SearchPartUseLocalDataSourceOptions<TDataItem, TConfiguration, TGlobalContext>
	& PaginationPartUseLocalDataSourceOptions<TConfiguration>
	& GlobalContextPartUseLocalDataSourceOptions<TGlobalContext>;

type SortingPartUseLocalDataSourceOptions<TDataItem> = {
	sorting?: DataSourceSortableState<TDataItem>;
};

type HasFilteringPartUseLocalDataSourceOptions<TFilterState> = { filtering: TFilterState; };
type FilteringPartUseLocalDataSourceOptions<TDataItem, TConfiguration, TGlobalContext> =
	TConfiguration extends HasFilteringPartLocalDataSourceConfiguration<TDataItem, infer TFilterState, TGlobalContext>
		? HasFilteringPartUseLocalDataSourceOptions<TFilterState>
		: {};

type HasSearchPartUseLocalDataSourceOptions = { searchTerm: string; };
type SearchPartUseLocalDataSourceOptions<TDataItem, TConfiguration, TGlobalContext> =
	TConfiguration extends HasSearchPartLocalDataSourceConfiguration<TDataItem, TGlobalContext>
		? HasSearchPartUseLocalDataSourceOptions
		: {};

type PaginationOptions = {
	currentPageNumber: number;
	amountPerPage?: number;
};
const DEFAULT_AMOUNT_PER_PAGE = 25;

type HasPaginationPartUseLocalDataSourceOptions = { pagination: PaginationOptions; };
type PaginationPartUseLocalDataSourceOptions<TConfiguration> =
	TConfiguration extends HasPaginationPartLocalDataSourceConfiguration
		? HasPaginationPartUseLocalDataSourceOptions
		: {};

type GlobalContextPartUseLocalDataSourceOptions<TGlobalContext> =
	TGlobalContext extends void
		? { globalContext?: undefined; }
		: { globalContext: TGlobalContext; };

type BaseLocalDataSource<TDataItem> = IDataSource<TDataItem>;
type PaginationResult<TConfiguration> =
	TConfiguration extends HasPaginationPartLocalDataSourceConfiguration ? IPageable : { };

type LocalDataSource<TDataItem, TConfiguration> =
	& BaseLocalDataSource<TDataItem>
	& PaginationResult<TConfiguration>;

class LocalDataSourceConfiguratorImplementation<TDataItem, TFilterState, TGlobalContext>
	implements LocalDataSourceConfigurator<TDataItem, TFilterState, TGlobalContext> {

	public setConfiguration<TConfiguration extends LocalDataSourceConfiguration<TDataItem, TFilterState, TGlobalContext>>(
		configuration: TConfiguration
	): ResultPartLocalDataSourceBuilder<TDataItem, TConfiguration, TGlobalContext> {
		return new LocalDataSourceBuilderImplementation<TDataItem, TFilterState, TConfiguration, TGlobalContext>(
			configuration
		);
	}
}

class LocalDataSourceBuilderImplementation<
	TDataItem,
	TFilterState,
	TConfiguration extends LocalDataSourceConfiguration<TDataItem, TFilterState, TGlobalContext>,
	TGlobalContext
> implements
	ResultPartLocalDataSourceBuilder<TDataItem, TConfiguration, TGlobalContext>
{
	private readonly _configuration: TConfiguration;

	constructor(configuration: TConfiguration) {
		this._configuration = configuration;
	}

	public getHook(): UseLocalDataSource<TDataItem, TConfiguration, TGlobalContext> {
		return (
			items: ReadonlyArray<TDataItem>,
			options: UseLocalDataSourceOptions<TDataItem, TConfiguration, TGlobalContext>
		): LocalDataSource<TDataItem, TConfiguration> => {

			const memorizedOptions = useValueEqualMemo(
				(): UseLocalDataSourceOptions<TDataItem, TConfiguration, TGlobalContext> => options,
				[options]
			);

			return useMemo(
				(): LocalDataSource<TDataItem, TConfiguration> => {
					let resultItems: ReadonlyArray<TDataItem> = items;
					let paginationResult: PaginationResult<TConfiguration> = {} as PaginationResult<TConfiguration>;

					const configuration: TConfiguration = this._configuration;

					// NOTE: globalContext can only be undefined if TGlobalContext = void
					const globalContext = memorizedOptions.globalContext as TGlobalContext;

					if (doesSupportFiltering<TDataItem, TFilterState, TGlobalContext>(configuration)) {
						if (!areFilteringOptionsProvided<TConfiguration, TFilterState>(memorizedOptions)) {
							throw new Error("Filtering options are not provided for the filterable data source.");
						}

						const {
							filtering: filteringConfiguration
						} = configuration;

						const {
							filtering: filteringOptions
						} = memorizedOptions;

						resultItems = filteringConfiguration.applyFilter(resultItems, filteringOptions, globalContext);
					}

					if (doesSupportSearch<TDataItem, TFilterState, TGlobalContext>(configuration)) {
						if (!areSearchOptionsProvided(memorizedOptions)) {
							throw new Error("Search options are not provided for the data source with search support.");
						}

						const {
							search
						} = configuration;

						let {
							searchTerm
						} = memorizedOptions;

						searchTerm = normalizeForSearch(searchTerm);

						if (searchTerm) {
							resultItems = resultItems.filter(i => search.doesMatchItem(i, searchTerm, globalContext));
						}
					}

					if (memorizedOptions.sorting) {
						resultItems = applySort(resultItems, memorizedOptions.sorting);
					}

					if (doesSupportPagination<TDataItem, TFilterState, TGlobalContext>(configuration)) {
						if (!arePaginationOptionsProvided(memorizedOptions)) {
							throw new Error("Pagination options are not provided for the pageable data source.");
						}

						const {
							pagination
						} = memorizedOptions;

						const {
							currentPageNumber,
							amountPerPage = DEFAULT_AMOUNT_PER_PAGE
						} = pagination;

						const itemsCount = resultItems.length;

						const pageable: IPageable = {
							pagination: calculatePaginationInfo(
								itemsCount,
								amountPerPage,
								currentPageNumber
							)
						};

						paginationResult = pageable as PaginationResult<TConfiguration>;

						const {
							firstItemIndex,
							lastItemIndex
						} = pageable.pagination;

						resultItems = resultItems.slice(firstItemIndex, lastItemIndex + 1);
					}

					return {
						...paginationResult,
						currentItems: resultItems
					};
				},
				[items, memorizedOptions]
			);
		};
	}
}

function doesSupportFiltering<TDataItem, TFilterState, TGlobalContext>(
	configuration: LocalDataSourceConfiguration<TDataItem, TFilterState, TGlobalContext>
): configuration is HasFilteringPartLocalDataSourceConfiguration<TDataItem, TFilterState, TGlobalContext> {
	return Boolean(configuration.filtering);
}

function doesSupportSearch<TDataItem, TFilterState, TGlobalContext>(
	configuration: LocalDataSourceConfiguration<TDataItem, TFilterState, TGlobalContext>
): configuration is HasSearchPartLocalDataSourceConfiguration<TDataItem, TGlobalContext> {
	return Boolean(configuration.search);
}

function doesSupportPagination<TDataItem, TFilterState, TGlobalContext>(
	configuration: LocalDataSourceConfiguration<TDataItem, TFilterState, TGlobalContext>
): configuration is HasPaginationPartLocalDataSourceConfiguration {
	return Boolean(configuration.hasPagination);
}

function areFilteringOptionsProvided<TConfiguration, TFilterState>(
	options: HasFilteringPartUseLocalDataSourceOptions<TFilterState> | {}
): options is HasFilteringPartUseLocalDataSourceOptions<TFilterState> {
	return Boolean((options as HasFilteringPartUseLocalDataSourceOptions<TConfiguration>).filtering);
}

function areSearchOptionsProvided(
	_options: HasSearchPartUseLocalDataSourceOptions | {}
): _options is HasSearchPartUseLocalDataSourceOptions {
	// searchTerm could be unset even if data source supports it
	return true;
}

function arePaginationOptionsProvided(
	options: HasPaginationPartUseLocalDataSourceOptions | {}
): options is HasPaginationPartUseLocalDataSourceOptions {
	return Boolean((options as HasPaginationPartUseLocalDataSourceOptions).pagination);
}

function applySort<TDataItem>(items: ReadonlyArray<TDataItem>, sorting: DataSourceSortableState<TDataItem>): ReadonlyArray<TDataItem> {
	if (sorting.direction === DataSourceSortDirection.None) {
		return items;
	}

	const propertyPathResolver: (value: TDataItem) => any = getPropertyPathResolver(
		sorting.propertyName,
		"propagate"
	);

	return [...items].sort((a, b) =>
		nativeSortFunction(
			propertyPathResolver(a),
			propertyPathResolver(b),
			sorting.direction
		)
	);
}

export function calculatePaginationInfo(
	itemsCount: number,
	amountPerPage: number,
	currentPageNumber: number
): PaginationInfo {
	const pagesCount: number = Math.ceil(itemsCount / amountPerPage);
	const lastPage: number = pagesCount - 1;
	const lastIndex: number = itemsCount - 1;
	const pageNumber: number = pagesCount > 0 && currentPageNumber + 1 > pagesCount
		? lastPage
		: currentPageNumber;

	const firstItemIndex: number = itemsCount === 0 || pageNumber * amountPerPage > lastIndex
		? -1
		: pageNumber * amountPerPage;

	const lastPageItemIndex: number = (pageNumber + 1) * amountPerPage - 1;
	const lastItemIndex: number = firstItemIndex === -1
		? -1
		: lastPageItemIndex > lastIndex
			? lastIndex
			: lastPageItemIndex;

	return {
		firstItemIndex,
		lastItemIndex,
		itemsCount,
		pageNumber,
		pagesCount
	};
}
